import { GridColDef, GridRowId } from '@mui/x-data-grid-premium';
import React, { useEffect, useState } from 'react';
import { tableSettingsProps, useCompleteTableSettings, useInitialTableSettings, useTableSettings } from '../tableSettings/useTableSettings';
import tableHelper from 'src/utils/tableHelper';
import { useGetAllSupplierProducts } from '../../api/requestConfigs/purchasing';
import { ID_FIELD_SUPPLIER_PRODUCTS } from 'src/utils/idFields';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useUpdateEffect from 'src/hooks/useUpdateEffect';

export const useSupplierProductsTable = (): [columns: GridColDef[], tableSettings: tableSettingsProps] => {

  const { rows, status } = useGetAllSupplierProducts();

  ////console.log(rows);
  const idPath = ID_FIELD_SUPPLIER_PRODUCTS;
  const tableName = "SupplierProductsTable";
  const initialTableSettings = useInitialTableSettings({ tableName, idPath, status }, rows);
  const columns: GridColDef[] = supplierProductTableColumns(initialTableSettings);
  const tableSettings = useCompleteTableSettings({ columns }, initialTableSettings);
  tableSettings.initalSorting = [{ field: "SupplierAssortmentItemId", sort: 'desc' }];
  useEffect(() => {
    tableSettings.setColumnVisibilityModel({
      Manufacturer_Key: false,
      ManufacturerProduct_Key: false,
      UpdatedAt: false,
      OrganizationCurrency_Key: false,
      SupplierAssortment_Key: false,
    })

  }, []);
  return [columns, tableSettings];
};


const supplierProductTableColumns = (tableSettings): GridColDef[] => {
  return React.useMemo(
    () => [
      { field: 'actions', getActions: (params) => tableHelper.buttonColumn(params, "Edit", "/products/supplier-products/edit?id=", "SupplierAssortmentItemId", "Edit Supplier Product"), type: 'actions', headerName: 'Edit', minWidth: 80, maxWidth: 120, flex: 0.8, },

      {
        field: 'SupplierAssortmentItemId',
        headerClassName: "column-header-demo",
        headerName: 'Supplier Product Id',
        width: 200,
        renderHeader: (params) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: "250px" }}>
            <span style={{ flexGrow: 1 }}>{params?.colDef?.headerName}</span>
            <IconButton
              id="demo-column-menu-button"
              aria-label="column menu"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                tableSettings.apiRef.current.showColumnMenu(params?.colDef?.field);
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </div>
        )
      }
      ,
      //{ field: 'SupplierAssortmentItemId',headerClassName: "column-header-demo",  headerName: 'Supplier Product Id'},
      { field: 'SupplierProduct_Key', headerName: 'Supplier Product', valueGetter: tableHelper.getFieldKeyOrValue, width: 220 },
      { field: 'SupplierAssortment_Value', headerName: 'Supplier Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 140, minWidth: 130, maxWidth: 180 },
      { field: 'Status_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, cellClassName: tableHelper.statusColumn, width: 75, maxWidth: 125, minWidth: 65, renderCell: tableHelper.renderStatusCell, },
      { field: 'Product_Key', headerName: 'Product Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 125, minWidth: 100, maxWidth: 150 },
      { field: 'Product_Value', headerName: 'Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 150, minWidth: 125, maxWidth: 175 },

      { field: 'Price', headerName: 'Price', valueFormatter: tableHelper.getCurrency, width: 75, minWidth: 60, maxWidth: 110 },
      { field: 'CurrencyConvertedPrice', headerName: 'Currency Converted Price', valueFormatter: tableHelper.getCurrency, width: 190, minWidth: 160, maxWidth: 220 },
      { field: 'Currency_Value', headerName: 'Currency', valueGetter: tableHelper.getFieldKeyOrValue },
      { field: 'Internal', headerName: 'Internal', type: "boolean", width: 70, minWidth: 60, maxWidth: 100 },
      { field: 'QuantityAvailableAtSupplier', headerName: 'Quantity', width: 75, minWidth: 65, maxWidth: 110 },
      { field: 'ManufacturerProduct_Value', headerName: 'Manufacturer Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 205, minWidth: 185, maxWidth: 225 },
      { field: 'ManufacturerProduct_Key', headerName: 'Manufacturer Product ID', valueGetter: tableHelper.getFieldKeyOrValue, width: 200, minWidth: 180, maxWidth: 220 },
      { field: 'Manufacturer_Value', headerName: 'Manufacturer Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 150, minWidth: 130, maxWidth: 180 },
      { field: 'Manufacturer_Key', headerName: 'Manufacturer Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 125, minWidth: 100, maxWidth: 150 },
      { field: 'TARIC', headerName: 'TARIC', width: 110, minWidth: 100, maxWidth: 130 },
      { field: 'ETA', headerName: 'ETA', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 110, minWidth: 100, maxWidth: 130 },
      { field: 'EAN', headerName: 'Barcode(EAN)', width: 110, minWidth: 100, maxWidth: 130 },
      { field: 'Modified', headerName: 'Modify Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'Created', headerName: 'Create Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'ValidFrom', headerName: 'Valid From', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'ValidTo', headerName: 'Valid To', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'UpdatedAt', headerName: 'Updated At', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'OrganizationCurrency_Value', headerName: 'Organization Currency', valueGetter: tableHelper.getFieldKeyOrValue, width: 150, minWidth: 125, maxWidth: 175 },
      { field: 'OrganizationCurrency_Key', headerName: 'Organization CurrencyId', valueGetter: tableHelper.getFieldKeyOrValue, width: 175, minWidth: 125, maxWidth: 215 },
      { field: 'ProductType', headerName: 'ProductType', width: 125, minWidth: 100, maxWidth: 150 },
      { field: 'SupplierProduct_Value', headerName: 'Vendor Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 175, minWidth: 125, maxWidth: 225 },
      { field: 'SupplierAssortment_Key', headerName: 'Supplier Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 140, minWidth: 130, maxWidth: 180 },
    ],
    [],
  );
}

export const supplierProductTableByIdColumns = (): GridColDef[] => {
  return React.useMemo(
    () => [
      { field: 'actions', getActions: (params) => tableHelper.buttonColumn(params, "Edit", "/products/supplier-products/edit?id=", "SupplierAssortmentItemId", "Edit Supplier Product"), type: 'actions', headerName: 'Edit', minWidth: 80, maxWidth: 120, flex: 0.8, },
      {
        field: 'SupplierAssortmentItemId', headerClassName: "column-header-demo", headerName: 'Supplier Product Id', width: 200,
      },
      { field: 'SupplierAssortment_Value', headerName: 'Supplier Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 140, minWidth: 130, maxWidth: 180 },
      { field: 'Status_Value', headerName: 'Status', valueGetter: tableHelper.getFieldKeyOrValue, cellClassName: tableHelper.statusColumn, width: 75, maxWidth: 125, minWidth: 65, renderCell: tableHelper.renderStatusCell, },
      { field: 'Product_Key', headerName: 'Product Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 125, minWidth: 100, maxWidth: 150 },
      { field: 'Product_Value', headerName: 'Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 150, minWidth: 125, maxWidth: 175 },
      { field: 'Manufacturer_Value', headerName: 'Manufacturer Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 150, minWidth: 130, maxWidth: 180 },
      { field: 'SupplierProduct_Key', headerName: 'Supplier Product Number', valueGetter: tableHelper.getFieldKeyOrValue, width: 200, minWidth: 180, maxWidth: 220 },
      { field: 'Price', headerName: 'Price', valueFormatter: tableHelper.getCurrency, width: 75, minWidth: 60, maxWidth: 110 },
      { field: 'CurrencyConvertedPrice', headerName: 'Currency Converted Price', valueFormatter: tableHelper.getCurrency, width: 190, minWidth: 160, maxWidth: 220 },
      //{ field: 'Currency_Value', headerName: 'Currency', valueGetter: tableHelper.getFieldKeyOrValue, width: 110, minWidth: 60, maxWidth: 125 },
      { field: 'Internal', headerName: 'Internal', type: "boolean", width: 70, minWidth: 60, maxWidth: 100 },
      { field: 'QuantityAvailableAtSupplier', headerName: 'Quantity', width: 75, minWidth: 65, maxWidth: 110 },
      { field: 'ManufacturerProduct_Value', headerName: 'Manufacturer Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 205, minWidth: 185, maxWidth: 225 },
      { field: 'ManufacturerProduct_Key', headerName: 'Manufacturer Product ID', valueGetter: tableHelper.getFieldKeyOrValue, width: 200, minWidth: 180, maxWidth: 220 },

      { field: 'Manufacturer_Key', headerName: 'Manufacturer Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 125, minWidth: 100, maxWidth: 150 },
      { field: 'TARIC', headerName: 'TARIC', width: 110, minWidth: 100, maxWidth: 130 },
      { field: 'ETA', headerName: 'ETA', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 110, minWidth: 100, maxWidth: 130 },
      { field: 'EAN', headerName: 'Barcode(EAN)', width: 110, minWidth: 100, maxWidth: 130 },
      { field: 'Modified', headerName: 'Modify Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'Created', headerName: 'Create Date', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'ValidFrom', headerName: 'Valid From', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'ValidTo', headerName: 'Valid To', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'UpdatedAt', headerName: 'Updated At', type: 'dateTime', valueGetter: tableHelper.getDateColumn, width: 120, minWidth: 100, maxWidth: 140 },
      { field: 'OrganizationCurrency_Value', headerName: 'Organization Currency', valueGetter: tableHelper.getFieldKeyOrValue, width: 150, minWidth: 125, maxWidth: 175 },
      { field: 'OrganizationCurrency_Key', headerName: 'Organization Currency Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 175, minWidth: 125, maxWidth: 215 },
      { field: 'ProductType', headerName: 'ProductType', width: 125, minWidth: 100, maxWidth: 150 },
      { field: 'SupplierProduct_Value', headerName: 'Vendor Product Name', valueGetter: tableHelper.getFieldKeyOrValue, width: 175, minWidth: 125, maxWidth: 225 },
      { field: 'SupplierAssortment_Key', headerName: 'Supplier Id', valueGetter: tableHelper.getFieldKeyOrValue, width: 140, minWidth: 130, maxWidth: 180 },
      
    ],
    [],
  );
}

export const useSupplierProductsOnProducts = (initialValues: any): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const [rows, setRows] = useState([])

  const idPath = ID_FIELD_SUPPLIER_PRODUCTS;

  useUpdateEffect(() => {
    if (rows?.length < 1) {
      setRows(initialValues?.SupplierProducts ?? [])
    }
  }, [initialValues?.SalesItems])
  const tableName = "SupplierProductsTable";
  const columns = supplierProductTableByIdColumns();
  const tableSettings = useTableSettings({ columns, rows, tableName, idPath });
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;
  useEffect(() => {
    tableSettings.setColumnVisibilityModel({
      ManufacturerProduct_Key: false,
      Manufacturer_Key: false,
      UpdatedAt: false,
      OrganizationCurrency_Key: false,
      SupplierAssortment_Key: false,
    });
  }, []);
  
  return [columns, tableSettings];
};

export const useSupplierProductsOnProductTable = (initialValues): [
  columns: GridColDef[],
  tableSettings: tableSettingsProps,
  changedProducts: any[],
  setChangedProducts: React.Dispatch<React.SetStateAction<any[]>>,
  showProductSelector: number | GridRowId,
  setShowProductSelector: React.Dispatch<React.SetStateAction<number | GridRowId>>
] => {
  const [changedSupplierProducts, setChangedSupplierProducts] = useState([]);
  const [showSupplierProductSelector, setShowSupplierProductSelector] = useState(-1);

  // Initialize from initialValues once
  useUpdateEffect(() => {
    if (changedSupplierProducts.length === 0) {
      setChangedSupplierProducts(initialValues?.SupplierProducts ?? []);
    }
  }, [initialValues?.SupplierProducts]);

  const columns: GridColDef[] = supplierProductTableByIdColumns();

  const tableSettings = useTableSettings({ columns, rows: changedSupplierProducts, idPath: ID_FIELD_SUPPLIER_PRODUCTS, tableName: 'SupplierProductsOnProductTable', });

  // Any additional optional settings
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hasToolbar = false;

  return [
    columns,
    tableSettings,
    changedSupplierProducts,
    setChangedSupplierProducts,
    showSupplierProductSelector,
    setShowSupplierProductSelector
  ];
};




export const useSupplierProductsSelectTable = (initialValues, showSupplierProductSelector): [columns: GridColDef[], tableSettings: tableSettingsProps] => {
  const { rows, status } = useGetAllSupplierProducts();
  const idPath = ID_FIELD_SUPPLIER_PRODUCTS;
  const tableName = "SupplierProductsSelectTable";
  const columns = supplierProductTableByIdColumns();
  const tableSettings = useTableSettings({ columns, rows, idPath, tableName, hasToggleMenu: true, status });
  useEffect(() => {
    //console.log("RUNNING SELECT TABLE FILTER", initialValues, tableSettings);
    if (showSupplierProductSelector == 1) {
      //   console.log("SETTING FILTER", initialValues?.Manufacturer);
      tableSettings?.setFilterModel({
        items: [
          { field: "Manufacturer_Value", operator: "equals", value: initialValues?.Manufacturer_Value },
          { field: "Product_Key", operator: "isEmpty", value: undefined }
        ]
      });
    }
  }, [showSupplierProductSelector]);
  return [columns, tableSettings];
};




